<!-- BaseInput.vue - Unified Input Component for Forms -->
<template>
  <div class="form-group">
    <!-- Label -->
    <label
      v-if="label"
      :for="inputId"
      class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
    >
      {{ label }}
      <span v-if="required" class="text-red-500 ml-1">*</span>
    </label>

    <!-- Input wrapper -->
    <div class="relative">
      <!-- Prefix icon -->
      <div
        v-if="prefixIcon"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <component :is="prefixIcon" class="h-5 w-5 text-gray-400 dark:text-gray-500" />
      </div>

      <!-- Textarea -->
      <textarea
        v-if="type === 'textarea'"
        :id="inputId"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :maxlength="maxlength"
        :rows="rows"
        :class="inputClasses"
        @blur="$emit('blur')"
        @focus="$emit('focus')"
        @input="handleInput"
        @change="$emit('change', $event)"
      ></textarea>

      <!-- Select -->
      <select
        v-else-if="type === 'select'"
        :id="inputId"
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        :class="inputClasses"
        @blur="$emit('blur')"
        @focus="$emit('focus')"
        @input="handleInput"
        @change="$emit('change', $event)"
      >
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>

      <!-- Standard Input -->
      <input
        v-else
        :id="inputId"
        :type="actualInputType"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :min="min"
        :max="max"
        :step="step"
        :maxlength="maxlength"
        :class="inputClasses"
        @blur="$emit('blur')"
        @focus="$emit('focus')"
        @input="handleInput"
        @change="$emit('change', $event)"
      />

      <!-- Suffix icon or action -->
      <div
        v-if="suffixIcon || showPasswordToggle"
        class="absolute inset-y-0 right-0 pr-3 flex items-center"
      >
        <!-- Password toggle -->
        <button
          v-if="showPasswordToggle"
          type="button"
          class="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
          @click="togglePasswordVisibility"
        >
          <component :is="passwordVisible ? EyeOff : Eye" class="h-5 w-5" />
        </button>
        
        <!-- Suffix icon -->
        <component
          v-else-if="suffixIcon"
          :is="suffixIcon"
          class="h-5 w-5 text-gray-400 dark:text-gray-500"
        />
      </div>
    </div>

    <!-- Helper text -->
    <p
      v-if="helperText && !error"
      class="mt-2 text-sm text-gray-500 dark:text-gray-400"
    >
      {{ helperText }}
    </p>

    <!-- Error message -->
    <p
      v-if="error"
      class="mt-2 text-sm text-red-600 dark:text-red-400 font-medium"
    >
      {{ error }}
    </p>

    <!-- Character count -->
    <p
      v-if="showCharCount && maxlength"
      class="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right"
    >
      {{ String(inputValue)?.length || 0 }}/{{ maxlength }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Eye, EyeOff } from 'lucide-vue-next'

export interface Option {
  value: string | number
  label: string
}

export interface Props {
  modelValue?: string | number
  label?: string
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'textarea' | 'select'
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  error?: string
  helperText?: string
  prefixIcon?: any
  suffixIcon?: any
  min?: number
  max?: number
  step?: number
  maxlength?: number
  rows?: number
  options?: Option[]
  showCharCount?: boolean
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: '',
  type: 'text',
  placeholder: '',
  disabled: false,
  readonly: false,
  required: false,
  error: '',
  helperText: '',
  prefixIcon: undefined,
  suffixIcon: undefined,
  min: undefined,
  max: undefined,
  step: undefined,
  maxlength: undefined,
  rows: 3,
  options: () => [],
  showCharCount: false,
  id: undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  blur: []
  focus: []
  change: [event: Event]
  input: [value: string | number]
}>()

// Password visibility toggle
const passwordVisible = ref(false)
const showPasswordToggle = computed(() => props.type === 'password')

const togglePasswordVisibility = () => {
  passwordVisible.value = !passwordVisible.value
}

// Computed properties
const inputId = computed(() => props.id || `input-${Math.random().toString(36).substr(2, 9)}`)

const actualInputType = computed(() => {
  if (props.type === 'password') {
    return passwordVisible.value ? 'text' : 'password'
  }
  return props.type
})

const inputValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const inputClasses = computed(() => {
  const baseClasses = [
    'block',
    'w-full',
    'text-base',
    'border',
    'rounded-xl',
    'shadow-sm',
    'transition-all',
    'duration-200',
    'ease-in-out',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-blue-500/20',
    'focus:border-blue-500',
    'bg-white',
    'dark:bg-gray-700',
    'text-gray-900',
    'dark:text-white',
    'placeholder-gray-500',
    'dark:placeholder-gray-400'
  ]

  // Padding classes based on icons
  const paddingClasses = []
  if (props.prefixIcon) {
    paddingClasses.push('pl-10')
  } else {
    paddingClasses.push('px-4')
  }

  if (props.suffixIcon || showPasswordToggle.value) {
    paddingClasses.push('pr-10')
  } else if (!props.prefixIcon) {
    paddingClasses.push('px-4')
  }

  // Vertical padding
  if (props.type === 'textarea') {
    paddingClasses.push('py-3')
  } else {
    paddingClasses.push('py-3')
  }

  // State classes
  const stateClasses = []
  if (props.error) {
    stateClasses.push(
      'border-red-500',
      'focus:border-red-500',
      'focus:ring-red-500/20'
    )
  } else {
    stateClasses.push(
      'border-gray-300',
      'dark:border-gray-600',
      'hover:border-gray-400',
      'dark:hover:border-gray-500'
    )
  }

  if (props.disabled) {
    stateClasses.push(
      'bg-gray-50',
      'dark:bg-gray-800',
      'text-gray-500',
      'dark:text-gray-400',
      'cursor-not-allowed'
    )
  }

  return [...baseClasses, ...paddingClasses, ...stateClasses]
})

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  let value: string | number = target.value
  
  if (props.type === 'number') {
    value = target.value === '' ? '' : Number(target.value)
  }
  
  emit('update:modelValue', value)
  emit('input', value)
}
</script>

<style scoped>
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* Custom focus states */
input:focus,
textarea:focus,
select:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
</style>
